// Кнопка. Две независимые оси — начертание и тон, — поэтому модификаторы
// не комбинируются в имена вида «primary», а накладываются друг на друга.

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-snug);
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast),
    color var(--transition-fast);

  &:focus-visible {
    outline: var(--focus-ring) solid var(--color-accent);
    outline-offset: 2px;
  }

  // Отключённая кнопка остаётся читаемой: полупрозрачность до неразличимости
  // прячет и то, что кнопка вообще существует.
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}

// ----- Размеры ---------------------------------------------------------
// Высота фиксирована, чтобы кнопки в одном ряду выстраивались по базовой
// линии независимо от того, есть ли внутри иконка.

.button--sm {
  height: 28px;
  padding: 0 10px;
  font-size: var(--text-sm);
  border-radius: var(--radius-sm);
}

.button--md {
  height: 34px;
  padding: 0 14px;
  font-size: var(--text-md);
}

.button--lg {
  height: 42px;
  padding: 0 20px;
  font-size: var(--text-base);
}

// ----- Только иконка ---------------------------------------------------
// Квадрат по высоте своего размера: иначе иконка болтается в широкой кнопке.

.button--icon-only {
  padding: 0;

  &.button--sm {
    width: 28px;
  }

  &.button--md {
    width: 34px;
  }

  &.button--lg {
    width: 42px;
  }
}

.button--full {
  display: flex;
  width: 100%;
}

// ----- Начертание × тон ------------------------------------------------

.button--solid {
  &.button--accent {
    background: var(--color-accent);
    border-color: var(--color-accent);
    color: var(--color-on-accent);

    &:hover:not(:disabled) {
      background: var(--color-accent-hover);
      border-color: var(--color-accent-hover);
    }
  }

  &.button--neutral {
    background: var(--color-text);
    border-color: var(--color-text);
    color: var(--color-surface);

    &:hover:not(:disabled) {
      opacity: 0.88;
    }
  }

  &.button--danger {
    background: var(--color-error);
    border-color: var(--color-error);
    color: var(--color-on-error);

    &:hover:not(:disabled) {
      background: var(--color-error-hover);
      border-color: var(--color-error-hover);
    }
  }
}

.button--outline {
  background: var(--color-surface);

  &.button--accent {
    border-color: var(--color-accent-border);
    color: var(--color-accent);

    &:hover:not(:disabled) {
      background: var(--color-accent-muted);
      border-color: var(--color-accent);
    }
  }

  &.button--neutral {
    border-color: var(--color-border);
    color: var(--color-text);

    &:hover:not(:disabled) {
      background: var(--color-elevated);
      border-color: var(--color-text-disabled);
    }
  }

  &.button--danger {
    border-color: var(--color-error-border);
    color: var(--color-error);

    &:hover:not(:disabled) {
      background: var(--color-error-muted);
      border-color: var(--color-error);
    }
  }
}

.button--ghost {
  background: transparent;

  &.button--accent {
    color: var(--color-accent);

    &:hover:not(:disabled) {
      background: var(--color-accent-muted);
    }
  }

  &.button--neutral {
    color: var(--color-text-secondary);

    &:hover:not(:disabled) {
      background: var(--color-elevated);
      color: var(--color-text);
    }
  }

  &.button--danger {
    color: var(--color-error);

    &:hover:not(:disabled) {
      background: var(--color-error-muted);
    }
  }
}

// Пунктирная — приглашение что-то добавить: «Добавить сайт», «Добавить поле»
// в конструкторе схем. Пунктир читается как «здесь пока пусто, но можно».
.button--dashed {
  background: transparent;
  border-style: dashed;
  font-weight: var(--weight-medium);

  &.button--accent {
    border-color: var(--color-accent-border);
    color: var(--color-accent);

    &:hover:not(:disabled) {
      background: var(--color-accent-muted);
      border-color: var(--color-accent);
    }
  }

  &.button--neutral {
    border-color: var(--color-border);
    color: var(--color-text-secondary);

    &:hover:not(:disabled) {
      border-color: var(--color-text-disabled);
      color: var(--color-text);
    }
  }

  &.button--danger {
    border-color: var(--color-error-border);
    color: var(--color-error);

    &:hover:not(:disabled) {
      background: var(--color-error-muted);
      border-color: var(--color-error);
    }
  }
}

// Как ссылка — для действий внутри текста и в тостах («Отменить»).
// Высота и внутренние отступы обнуляются, иначе она не встаёт в строку текста.
.button--link {
  height: auto;
  padding: 0;
  background: transparent;
  border-color: transparent;
  border-radius: var(--radius-sm);

  &.button--accent {
    color: var(--color-accent);
  }

  &.button--neutral {
    color: var(--color-text-secondary);
  }

  &.button--danger {
    color: var(--color-error);
  }

  &:hover:not(:disabled) {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

// ----- Нажатое состояние ----------------------------------------------
// Для панелей редактора: «жирный», «курсив» — кнопки с памятью.

.button--pressed {
  &.button--ghost,
  &.button--outline {
    background: var(--color-accent-muted);
    border-color: var(--color-accent-border);
    color: var(--color-accent);
  }
}

// ----- Загрузка --------------------------------------------------------

.button--loading {
  cursor: progress;
}

.button__spinner {
  flex: none;
  animation: button-spin 800ms linear infinite;
}

.button__icon {
  flex: none;
  display: inline-flex;

  svg {
    width: 16px;
    height: 16px;
  }
}

.button--sm .button__icon svg,
.button--sm.button--icon-only > svg {
  width: 14px;
  height: 14px;
}

.button--lg .button__icon svg,
.button--lg.button--icon-only > svg {
  width: 18px;
  height: 18px;
}

// Кнопка-иконка: содержимое — сама иконка, без обёртки.
.button--icon-only > svg {
  width: 16px;
  height: 16px;
}

@keyframes button-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .button__spinner {
    animation-duration: 2s;
  }
}
